import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { jobTitleLabel } from '../../utils/displayName';
import PopupHomeNotice from '../PopupHomeNotice';
import { usePopupHome, HOME_JOBS } from '../../services/popupHome';   // Active Jobs, one tab only (report c2846304)
import CloseNowPrompt, { CloseCheck, closePromptHeldByOther, onClosePromptChange } from '../Jobs/CloseNowPrompt';

// ── "Ready to close?" wherever the operator is (Calem, report dd0ed51a) ─────────────────────────
// "When a job is complete, the email has gone to the client, including all of the attachments, and it
// is ready to be closed. Automatically pop up the request to close the job rather than popping up on
// clicking the job." The prompt itself is not new — it lived inside the job panel, so it could only
// ever appear once the operator had opened the job. A job that became closable while they were in
// their mailbox, or in Filing, surfaced nowhere. This watcher is mounted once, app-wide, beside
// JobStatusConfirm and polled exactly the same way, and it shows the SAME prompt (Jobs/CloseNowPrompt)
// for the caller's own closable jobs (GET /jobs/close-prompt/mine).
// Only jobs that really can be closed now come back from that poll: a job still missing a closing step
// or with money owed is never thrown at someone out of nowhere — the in-panel prompt keeps telling
// that story when the job is opened. One job at a time, and the in-panel prompt wins if both are up
// (the registry in CloseNowPrompt). Dismissing is session-only, as in the panel: it asks again later.
const POLL_MS = 60_000;
// Don't steal the keyboard from someone mid-sentence — keyed on keystrokes, never on focus (see the
// regression noted in JobStatusConfirm).
const TYPING_IDLE_MS = 4000;
const COMPLETE = '4. Complete';
const WATCHER_TAG = 'watcher';

type Row = { jobId: string; jobNo: number; client: string; specific: string; service: string; sentAt: number; check: CloseCheck };

export default function CloseJobWatcher() {
  const { t } = useTranslation();
  const loc = useLocation();
  const me = useStore(s => s.me);
  const showToast = useStore(s => s.showToast);
  // Never on the public/no-auth pages: a client on /w/<token> must not be asked to close our jobs.
  const publicPage = /^\/(login|w|dd|contract|portal)(\/|$)/.test(loc.pathname);
  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    // Falls back to false on its own — never a state something can get stuck in.
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, []);
  const [queue, setQueue] = useState<Row[]>([]);
  // Said "Not now" to this job: gone for this browser session, exactly as in the job panel.
  const [skipped, setSkipped] = useState<string[]>([]);
  const meRef = useRef(me);
  meRef.current = me;

  const poll = useCallback(async () => {
    const m = meRef.current;
    // Only people who can open Jobs are ever asked (the gate on /api/jobs): anyone else's call would
    // be refused, and a refused call pages the admins.
    if (!m || !localStorage.getItem('mf_token') || !(m.modules || []).some(k => k === 'active_jobs' || k === 'closed_jobs')) return;
    try {
      const { rows } = await api.jobClosePromptMine();
      setQueue((rows || []) as Row[]);
    } catch { /* transient — try again next tick */ }
  }, []);

  useEffect(() => {
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);
  // Drop everything the moment the user logs out — never linger over the login screen with the
  // previous user's job and client.
  useEffect(() => { if (!me) { setQueue([]); setSkipped([]); } }, [me]);
  // A fresh look whenever the operator moves around (don't wait for the 60s tick).
  useEffect(() => { poll(); }, [loc.pathname, me?.userId, poll]);
  // And the moment a job panel's own prompt appears or goes, so the two never stack for a second.
  const [, bump] = useState(0);
  useEffect(() => onClosePromptChange(() => bump(n => n + 1)), []);
  // A job closed here, or closed in the panel, leaves the queue on the next look.
  useEffect(() => {
    const h = () => { void poll(); };
    window.addEventListener('slf:jobs-changed', h);
    return () => window.removeEventListener('slf:jobs-changed', h);
  }, [poll]);

  // One at a time, and never the job a panel prompt is already asking about.
  const current = queue.find(r => !skipped.includes(r.jobId) && !closePromptHeldByOther(r.jobId, WATCHER_TAG));
  // Report c2846304: a job is closed on Active Jobs, so the prompt is put up in the tab showing that page
  // and in no other; with the page open in no tab, the tab in front offers to open it (PopupHomeNotice).
  const pending = !!me && !!current && !publicPage && !typing;
  const home = usePopupHome(HOME_JOBS, pending);
  if (!pending || !current) return null;
  if (home !== 'show') return home === 'notice' ? <PopupHomeNotice page={HOME_JOBS} /> : null;

  // The job's title as the Jobs lists show it (jobTitleLabel): no "【1】", "(2)" for any other quantity.
  const identity = [`#${Number(current.jobNo).toLocaleString()}`, String(current.client || '').trim(),
    jobTitleLabel(current.specific) || String(current.service || '').trim()].filter(Boolean).join(' · ');

  return (
    // Same layer as the other app-wide job pop-ups but mounted before JobStatusConfirm (App.tsx), so a
    // status email waiting on its confirmation stays on top of this.
    <CloseNowPrompt key={current.jobId} jobId={current.jobId} processLevel={COMPLETE}
      completeEmailSentAt={Number(current.sentAt) || 1} check={current.check} identity={identity}
      tag={WATCHER_TAG} zIndex={5500}
      onDismissed={() => setSkipped(s => s.concat(current.jobId))}
      onClosed={() => {
        showToast(t('jb_close_prompt_closed_toast'));
        setSkipped(s => s.concat(current.jobId));
        setQueue(q => q.filter(r => r.jobId !== current.jobId));
        // Active Jobs, and the job's panel if it is open, pick up "6. Closed" at once.
        window.dispatchEvent(new CustomEvent('slf:jobs-changed', { detail: { jobId: current.jobId } }));
      }} />
  );
}
